/** The Checks tab: every check the filing is subject to, in every state (ADR-0163).
 *
 * `Findings` lists what failed. This lists what was *checked*: one row per check, six states, in the
 * four named parts the work runs in. Three properties are the reason it exists.
 *
 * - **A check nobody ran is not a pass.** `not_evaluated` and `waiting` are their own words and
 *   their own count (`not_run`), included in the total. The summary is the server's sentence
 *   ("N of M checks passed; K not run yet"); this screen divides nothing and prints no percentage.
 * - **"Checked just now" is the only claim made about when.** Nothing is stored and nothing ran
 *   earlier, so there is no "last run 08:02". The stamp is the server's clock at this read, with the
 *   ledger position and rules version the checks were derived from, and *Check again* is reading
 *   again.
 * - **A fix is a pointer at an act that exists**, named for the reader's role. A role that cannot
 *   take it is told so; nothing here offers a control that predictably refuses.
 *
 * A failed read says so and offers a retry; it is never drawn as "no checks".
 */

import { Link } from "@tanstack/react-router";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { useState } from "react";

import type { FilingCheck, FilingChecks } from "../api/filingChecks";
import { useFilingChecks } from "../api/filingChecks";
import type { Obligation, Scope2Method } from "../api/queries";
import { useReader } from "../api/profile";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { SkeletonRows } from "../components/ui/Skeleton";
import { StatusChip } from "../components/ui/StatusChip";
import { supportLevelWords } from "../lib/customerWords";
import { instantStamp } from "../lib/format";
import styles from "./ReportsChecks.module.css";
import { fixLinkFor, groupByPart, isOpen, NOT_PERMITTED, wordOf } from "./filingChecksModel";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";

type Show = "open" | "passed" | "all";

function FixLine({ fix, filing }: { fix: FilingCheck["fix_items"][number]; filing: string }) {
  const link = fixLinkFor(fix);
  return (
    <li className={styles.fix} data-testid="check-fix" data-action={fix.action}>
      <span className={styles.fixDetail}>{fix.detail}</span>
      {fix.permitted ? (
        <Link
          to={link.to}
          search={(prev: Partial<ScreenSearch>) => ({
            ...keepSearch(prev),
            ...(link.to === "/reports" ? { filing, tab: link.tab } : link.tab ? { tab: link.tab } : {}),
            ...link.search,
          })}
          className={styles.fixLink}
        >
          {link.label}
        </Link>
      ) : (
        <span className={styles.refused}>{link.label}: {NOT_PERMITTED}</span>
      )}
    </li>
  );
}

function CheckRow({ check, titleOf, filing }: { check: FilingCheck; titleOf: (key: string) => string; filing: string }) {
  const state = wordOf(check.state);
  const lead = check.state === "passed" || check.state === "not_applicable" ? check.detail : check.message;
  return (
    <li className={styles.check} data-testid="check" data-state={check.state} data-key={check.key}>
      <div className={styles.checkHead}>
        <StatusChip tone={state.tone} size="sm">
          {state.word}
        </StatusChip>
        <span className={styles.checkTitle}>{check.title}</span>
      </div>
      {lead ? <p className={styles.lead}>{lead}</p> : null}
      {check.waiting_on ? (
        <p className={styles.lead}>Waits for: {titleOf(check.waiting_on)}.</p>
      ) : null}
      {check.fix_items.length > 0 ? (
        <ul className={styles.fixes} aria-label={`How to clear: ${check.title}`}>
          {check.fix_items.map((fix) => (
            <FixLine key={fix.item_key} fix={fix} filing={filing} />
          ))}
          {check.fix_items_total > check.fix_items.length ? (
            <li className={styles.more}>
              {check.fix_items_total - check.fix_items.length} more not listed here.
            </li>
          ) : null}
        </ul>
      ) : null}
      <details className={styles.more2}>
        <summary>Why, and where it comes from</summary>
        {check.remedy && check.state !== "passed" ? <p>{check.remedy}</p> : null}
        {check.detail && check.detail !== lead ? <p>{check.detail}</p> : null}
        <p className={styles.cite}>
          {check.source_url ? (
            <a href={check.source_url} target="_blank" rel="noreferrer">
              {check.citation}
            </a>
          ) : (
            <>
              {check.citation}
              {check.source_url_reason ? ` (${check.source_url_reason})` : ""}
            </>
          )}
          {check.origin === "system" ? " · an EmitIQ check, not a regulation's" : ""}
        </p>
      </details>
    </li>
  );
}

function Body({ checks, active, onAgain, fetching }: { checks: FilingChecks; active: Obligation; onAgain: () => void; fetching: boolean }) {
  const reader = useReader();
  const [show, setShow] = useState<Show>("open");
  const [part, setPart] = useState<string | null>(null);
  const groups = groupByPart(checks.parts, checks.checks);
  const titleOf = (key: string) => checks.checks.find((check) => check.key === key)?.title ?? key;
  const open = checks.checks.filter(isOpen).length;
  const passed = checks.counts.passed ?? 0;
  const matches = (check: FilingCheck) =>
    (show === "all" || (show === "open" ? isOpen(check) : check.state === "passed")) &&
    (part === null || check.part === part);

  return (
    <>
      <Panel
        title="Checks"
        sub={checks.summary.sentence}
        column="1 / 13"
        action={
          <button type="button" className={styles.again} onClick={onAgain} disabled={fetching} data-testid="checks-again">
            <ArrowsClockwise size={14} aria-hidden="true" /> {fetching ? "Checking…" : "Check again"}
          </button>
        }
      >
        <ul className={styles.parts} aria-label="The four parts of the work" data-testid="checks-parts">
          {checks.parts.map((entry) => {
            const state = wordOf(entry.state);
            const selected = part === entry.part;
            return (
              <li key={entry.part}>
                <button
                  type="button"
                  className={styles.part}
                  aria-pressed={selected}
                  onClick={() => setPart(selected ? null : entry.part)}
                  data-part={entry.part}
                >
                  <span className={styles.partLabel}>{entry.label}</span>
                  <span className={`${styles.partCount} display`}>
                    {entry.passed} of {entry.total}
                  </span>
                  <StatusChip tone={state.tone} size="sm">
                    {entry.state === "not_applicable" ? "None here" : state.word}
                  </StatusChip>
                </button>
              </li>
            );
          })}
        </ul>

        <div className={styles.filters} role="group" aria-label="Show checks">
          {(
            [
              ["open", `Open ${open}`],
              ["passed", `Passed ${passed}`],
              ["all", `All ${checks.checks.length}`],
            ] as const
          ).map(([key, label]) => (
            <button key={key} type="button" aria-pressed={show === key} className={styles.filter} onClick={() => setShow(key)}>
              {label}
            </button>
          ))}
        </div>

        <div data-testid="checks-list">
          {groups.map((group) => {
            const rows = group.checks.filter(matches);
            if (rows.length === 0) return null;
            return (
              <section key={group.part.part} className={styles.group} aria-label={group.part.label}>
                <h3 className={styles.groupHead}>{group.part.label}</h3>
                <ul className={styles.list}>
                  {rows.map((check) => (
                    <CheckRow key={check.key} check={check} titleOf={titleOf} filing={active.obligation_id} />
                  ))}
                </ul>
              </section>
            );
          })}
          {checks.checks.filter(matches).length === 0 ? (
            <Empty>
              {show === "open"
                ? checks.summary.not_run > 0
                  ? "Nothing is failing, but some checks have not run. Show all to see them."
                  : "Every check that applies has passed. A pass is not an approval."
                : "No checks in this view."}
            </Empty>
          ) : null}
        </div>

        <p className={styles.stamp} data-testid="checks-stamp" aria-live="polite">
          Read {instantStamp(checks.evaluated_at, reader)} from the inventory as it stood
          {checks.inputs_as_of.ledger_head_seq !== null ? `, ledger position ${checks.inputs_as_of.ledger_head_seq}` : ""}
          , rules version {checks.inputs_as_of.rules_version}. Check again to read it afresh.{" "}
          {supportLevelWords(checks.support_level)}.
        </p>
      </Panel>
    </>
  );
}

export function ChecksTab({ active, method }: { active: Obligation; method: Scope2Method }) {
  const read = useFilingChecks(active.obligation_id, method);
  if (read.error && !read.data) {
    return (
      <Panel title="Checks" column="1 / 13">
        <ReadFailed
          of="This filing's checks"
          error={read.error}
          onRetry={() => void read.refetch()}
        />
      </Panel>
    );
  }
  if (!read.data) {
    return (
      <Panel title="Checks" column="1 / 13">
        <div role="status" aria-busy="true">
          <span className="visuallyHidden">Reading this filing&rsquo;s checks…</span>
          <SkeletonRows rows={5} lead />
        </div>
      </Panel>
    );
  }
  return (
    <Body
      checks={read.data}
      active={active}
      fetching={read.isFetching}
      onAgain={() => void read.refetch()}
    />
  );
}
